import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import Toast from "../../helpers/Toast";
import { Col, Row, Button, Input } from "reactstrap";
import InputComponent from "../../components/InputComponent";
import ButtonComponent from "../../components/ButtonComponent";
import { ClientQuotationService } from "../../services/ClientQuotationService";
import { ClientQuotationDTO } from "../../models/Quotation/ClientQuotationDTO";
import { PartsAndServicesQuotationDTO } from "../../models/Quotation/PartsAndServicesQuotationDTO";
import ReviewQuotationParts from "../../components/Quotation/ReviewQuotationParts";
import RoutesObject from "../../helpers/Routes";
import ModalComponent from "../../components/ModalComponent";

const ReviewClientQuotation = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token") ?? "";
  const intl = useIntl();
  const service = new ClientQuotationService();
  const [clientQuotation, setClientQuotation] = useState<ClientQuotationDTO>({} as ClientQuotationDTO);
  const [loading, setLoading] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>("");
  const openDate = clientQuotation.openDate ? new Date(clientQuotation.openDate) : null;
  const formattedOpenDate = openDate ? `${openDate.getDate()}-${openDate.getMonth() + 1}-${openDate.getFullYear()}` : '';
  const [parts, setParts] = useState<PartsAndServicesQuotationDTO[]>([]);
  const [requestChange, setRequestChange] = useState<boolean>(false);
  const [isRejection, setIsRejection] = useState<boolean>(false);
  const [userCanDecide, setUserCanDecide] = useState<boolean>(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [confirmButtonLeft, setConfirmButtonLeft] = useState("Sim");
  const [confirmButtonRight, setConfirmButtonRight] = useState("Não");
  const [onConfirmAction, setOnConfirmAction] = useState<() => Promise<void> | void>(() => {});
  const [onCancelAction, setOnCancelAction] = useState<() => void>(() => {});
  const [rejectionMotive, setRejectionMotive] = useState<string>("");
  
  function openConfirm(options: {
      title: string;
      text: string;
      buttonLeft?: string;
      buttonRight?: string;
      onConfirm: () => Promise<void> | void;
      onCancel?: () => void;
    }) {
      setConfirmTitle(options.title);
      setConfirmText(options.text);
      setConfirmButtonLeft(options.buttonLeft ?? "Sim");
      setConfirmButtonRight(options.buttonRight ?? "Não");
      setOnConfirmAction(() => options.onConfirm);
      setOnCancelAction(() => options.onCancel ?? (() => setConfirmOpen(false)));
      setConfirmOpen(true);
    }


  useEffect(() => {
    const fetchQuotation = async () => {
      setLoading(true);
      const response = await service.GetById(Number(id),token ?? undefined);
      if (response.success === false || response.obj === null) {
        if (response.message?.toLowerCase().includes("token")) {
          navigate(RoutesObject.invalidToken.details); // Redireciona
        } else {
        Toast.Show("error", response.message);
        }
        setLoading(false);
        return;
      }
      setParts(response.obj.partsAndServicesQuotations);
      setClientQuotation(response.obj);
      setLoading(false);
    };

    fetchQuotation();
  }, []);

  const formatPrice = (price: number) => {
    return price ? `${price.toFixed(2).replace('.', ',')} €` : '';
  };

  const handleAccept = async () => {
    openConfirm({
      title: intl.formatMessage({ id: "Screens.Quotation.Confirm.Accept.Title" }),
      text: intl.formatMessage({ id: "Screens.Quotation.Confirm.Accept.Text" }),
     
      onConfirm: async () => {

    const response = await service.AcceptClientQuotation(Number(id), { decision: true}, token);
    if (response.success) {
      Toast.Show("success", intl.formatMessage({ id: "Screens.Quotation.Accept.Success" }));
      setUserCanDecide(false);
    } else {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Quotation.Accept.Error" }));
    }
  }
    });
  }

  const handleReject = async () => {
    openConfirm({
      title: intl.formatMessage({ id: "Screens.Quotation.Confirm.Reject.Title" }),
      text: intl.formatMessage({ id: "Screens.Quotation.Confirm.Reject.Text" }),
      onConfirm: async () => {
    
    const response = await service.RejectClientQuotation(Number(id), {rejectionMotive: rejectionMotive, decision: false}, token);
    if (response.success) {
      Toast.Show("success", intl.formatMessage({ id: "Screens.Quotation.Reject.Success" }));
      setUserCanDecide(false);
    } else {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Quotation.Reject.Error" }));
    }
  }
    });
  }

  const handleRequestChanges = async () => {
    setRequestChange(!requestChange);
    setIsRejection(false);
  };
  const handleRejectMotive = async () => {
    setIsRejection(!isRejection);
    setRequestChange(false);
  };

  const handleSubmit = async () => {
    setRequestChange(!requestChange);
    const response = await service.RequestChanges(Number(id), { decision: false, changes: notes }, token );
    if (response.success) {
      Toast.Show("success", intl.formatMessage({ id: "Screens.Quotation.Modify.Success" }));
      setUserCanDecide(false);
    } else {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Quotation.Modify.Error" }));
    }
  };

  const handlePartsChange = () => {
    setParts(parts);
  };

  return (
    <div className="pageContainer">
      {/* Modal de confirmação */}
      <ModalComponent
        IsOpen={confirmOpen}
        title={confirmTitle}
        text={confirmText}
        buttonLeft={confirmButtonLeft}
        buttonRight={confirmButtonRight}
        OnClickLeft={async () => {
          setConfirmOpen(false);
          await onConfirmAction();
        }}
        OnClickRight={() => {
          setConfirmOpen(false);
          onCancelAction();
        }}
      />
      <div className="pageBody">
        <div className="pageHeader">                         
            <span className="pageTitle">
                <Row className="pageHeader" style={{ margin: "2%", display: "flex", alignItems: "center", justifyContent: "start" }}>
                    <Col xl={4} lg={4} md={4} sm={6} xs={6} style={{ display: "flex", margin: "0px" }}>
                        <FormattedMessage id="Screens.ClientQuotation" /> {clientQuotation?.id}
                    </Col>
                    <Col
                        xl={8}
                        lg={8}
                        md={8}
                        sm={6}
                        xs={6}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "end",
                        }}
                    >
                    {clientQuotation?.client?.name && clientQuotation?.client.name}
                    </Col>
                </Row>
            </span>
        </div>
            <Row style={{ margin: "2%", padding: "0px" }}>
                <span style={{ marginTop: "2%", fontWeight: "bold"}}>{intl.formatMessage({id: "Screens.ClientQuotation.Info.OpenDate"})}</span>
                <span>{formattedOpenDate}</span>
                <span style={{ marginTop: "2%", fontWeight: "bold"}}>{intl.formatMessage({id: "Screens.ClientQuotation.Info.Description"})}</span>
                <span>{clientQuotation.description}</span>
                <ReviewQuotationParts reloadParts={true} goToPage={0} currentPage={0} isComplete={true} onPartsChange={handlePartsChange} initialParts={parts} quotationType={clientQuotation.quotationType}/>
            </Row>
            <Row style={{ margin: "2%", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "start" }}>
                </Row>
                <Row style={{ margin: "2%", padding: "0px"}}>
                <Col xl={12} lg={12} md={12} sm={12} xs={12} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                            <span style={{ fontWeight: "bold" }}>
                                {intl.formatMessage({ id: "Screens.Quotation.Value" })}
                            </span>
                            <span>{formatPrice(clientQuotation.sellingPrice)}</span>
                        </div>
                    </Col>
                <Col xl={12} lg={12} md={12} sm={12} xs={12} style={{ marginTop: "-2%", margin: "2%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        {userCanDecide && (
                            <>
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.Quotation.Accept" })}
                                    onClick={handleAccept}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.Quotation.Reject" })}
                                    onClick={handleRejectMotive}
                                    />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.Quotation.Modify" })}
                                    onClick={handleRequestChanges}
                                    />
                            </>)}
                    </div>
                    </Col>
                    {requestChange && userCanDecide &&( 
                      <div>
                        <InputComponent
                            xl={12}
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            type={"textArea"}
                            value={notes}
                            label={intl.formatMessage({ id: "Screens.ClientQuotation.Info.Notes" })}
                            onChange={(t: string) => setNotes(t)}
                            readOnly={false}
                            />
                        <ButtonComponent
                            text={intl.formatMessage({ id: "Screens.Quotation.Submit" })}
                            onClick={handleSubmit}
                         />        
                       </div>       
                     )}
                      {isRejection && userCanDecide &&( 
                      <div>
                        <InputComponent
                            xl={12}
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            type={"textArea"}
                            value={rejectionMotive}
                            label={intl.formatMessage({ id: "Screens.ClientQuotation.Info.RejectionMotive" })}
                            onChange={(t: string) => setRejectionMotive(t)}
                            readOnly={false}
                            />
                        <ButtonComponent
                            text={intl.formatMessage({ id: "Screens.Quotation.Submit" })}
                            onClick={handleReject}
                         />        
                       </div>       
                     )}
                </Row>
            </div>
        </div>
  );
};

export default ReviewClientQuotation;
